import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ZoomControlComponent } from 'src/app/shared/components/ui/zoom-control/zoom-control.component';
import { ZoomableDirective } from 'src/app/shared/directives/zoomable.directive';

export interface MermaidDialogData {
  content: string;
  title: string;
  id: string;
}

@Component({
  selector: 'app-mermaid-dialog',
  standalone: true,
  imports: [MatDialogModule, ZoomControlComponent, ZoomableDirective],
  template: `
    <div class="mermaid-dialog-container">
      <div
        #zoom="appZoomable"
        appZoomable
        [zoomScale]="0.8"
        [minScale]="0.3"
        [maxScale]="5.0"
        class="mermaid-dialog-body"
        [class.grabbing]="zoom.isDragging()"
      >
        <app-zoom-control
          class="zoom-toolbar"
          [(zoomScale)]="zoom.zoomScale"
          [minScale]="0.3"
          [maxScale]="5.0"
          [showClose]="true"
          (close)="closeDialog()"
          (reset)="zoom.resetZoom()"
          (click)="$event.stopPropagation()"
        />

        <div
          #mermaidPreview
          class="mermaid-render-area"
          [style.transform]="'translate(' + zoom.panX() + 'px, ' + zoom.panY() + 'px) scale(' + zoom.zoomScale() + ')'"
          [style.transition]="zoom.isDragging() ? 'none' : 'transform 0.2s ease-out'"
          style="transform-origin: center center;"
        ></div>
      </div>
    </div>
  `,
  styles: [
    `
      .mermaid-dialog-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        background: #ffffff;
      }

      .mermaid-dialog-body {
        flex: 1;
        position: relative;
        overflow: hidden;
        cursor: grab;
        user-select: none;
        display: flex;
        justify-content: center;
        align-items: center;

        &.grabbing {
          cursor: grabbing;
        }
      }
      .mermaid-render-area {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0;
        width: 100%;
        height: 100%;
        box-sizing: border-box;

        ::ng-deep svg {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          box-sizing: border-box;
        }
      }
      .zoom-toolbar {
        position: absolute;
        top: 20px;
        right: 20px;
        z-index: 10;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MermaidDialogComponent implements AfterViewInit {
  data: MermaidDialogData = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<MermaidDialogComponent>);

  @ViewChild('mermaidPreview', { static: false }) mermaidPreviewEl!: ElementRef<HTMLDivElement>;

  ngAfterViewInit() {
    this.renderMermaid();
  }

  private async renderMermaid() {
    const content = this.data.content;
    if (!content) return;

    const elementId = `mermaid-dialog-${this.data.id}`.replace(/[^a-zA-Z0-9-]/g, '-');
    try {
      const mermaid = (await import('mermaid')).default;
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
      });

      const { svg } = await mermaid.render(elementId, content);

      if (this.mermaidPreviewEl) {
        this.mermaidPreviewEl.nativeElement.innerHTML = svg;
      }
    } catch (err) {
      console.error('Mermaid render error inside dialog:', err);
      const badEl = document.getElementById(elementId) || document.getElementById(`d${elementId}`);
      if (badEl) {
        badEl.remove();
      }
      if (this.mermaidPreviewEl) {
        this.mermaidPreviewEl.nativeElement.innerHTML = `<div style="color: red; padding: 20px;">Error rendering diagram: ${(err as Error).message}</div>`;
      }
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
